import React, { useCallback, useEffect, useState } from 'react';

import { SYSTEM_THEME_STORAGE_KEY, systemThemeInstance, THEME_STORAGE_KEY, themeInstance } from 'app/lib/localStorages';
import { isSSR } from 'app/packages/components';

import { Theme, ToggleThemeState } from '../constants';

type ThemeContextValue = {
  theme: Theme;
  toggleThemeState: ToggleThemeState;
  isSystemTheme: boolean;
  setTheme: (theme: ToggleThemeState) => void;
};

type ThemeProviderProps = {
  initialTheme: Theme;
};

const ThemeContext = React.createContext<ThemeContextValue>({
  theme: Theme.LIGHT,
  toggleThemeState: ToggleThemeState.LIGHT,
  isSystemTheme: false,
  setTheme: () => {},
});

const PREFER_SCHEME_DARK = '(prefers-color-scheme: dark)';
const matcher = !isSSR ? window.matchMedia(PREFER_SCHEME_DARK) : undefined;

export const useTheme = () => {
  return React.useContext(ThemeContext);
};

export const ThemeContextProvider: React.FC<React.PropsWithChildren<ThemeProviderProps>> = ({
  children,
  initialTheme = Theme.LIGHT,
}) => {
  const [theme, setThemeToContext] = useState<Theme>(initialTheme);
  const [systemTheme, setSystemTheme] = useState<boolean>(
    !isSSR && systemThemeInstance.get() || false,
  );

  const setThemeToHTML = (currentTheme: Theme, theme: Theme) => {
    const html = document.querySelector('html');
    if (html) {
      html.classList.remove(currentTheme);
      html.classList.add(theme);
    }
  };

  const setThemeToLocalStorage = (theme: Theme) => {
    themeInstance.set(theme);
  };

  const setTheme = useCallback((theme: Theme) => {
    setThemeToContext((currentTheme) => {
      setThemeToHTML(currentTheme, theme);
      setThemeToLocalStorage(theme);

      return theme;
    });
  }, []);

  const handleSetTheme = useCallback((theme: ToggleThemeState) => {
    if (theme === ToggleThemeState.SYSTEM) {
      setSystemTheme(true);
    }
    else {
      setSystemTheme(false);
      setTheme(theme === ToggleThemeState.DARK ? Theme.DARK : Theme.LIGHT);
    }
  }, []);

  const matchMediaHandler = useCallback(({ matches: isDark }: MediaQueryListEvent) => {
    setTheme(isDark ? Theme.DARK : Theme.LIGHT);
  }, []);


  const syncWithStorage = (storage: StorageEvent) => {
    if (storage.key === THEME_STORAGE_KEY || storage.key === SYSTEM_THEME_STORAGE_KEY) {
      setSystemTheme(Boolean(systemThemeInstance.get()));
      setTheme(themeInstance.get() || Theme.LIGHT);
    }
  };

  const getToggleThemeState = () => {
    if (systemTheme) {
      return ToggleThemeState.SYSTEM;
    }

    if (theme === Theme.DARK) {
      return ToggleThemeState.DARK;
    }

    return ToggleThemeState.LIGHT;
  };

  useEffect(() => {
    window.addEventListener('storage', syncWithStorage);

    return () => {
      window.removeEventListener('storage', syncWithStorage);
    };
  }, []);

  useEffect(() => {
    !isSSR && systemThemeInstance.set(systemTheme);

    if (systemTheme) {
      const isDarkTheme = matcher?.matches;
      setTheme(isDarkTheme ? Theme.DARK : Theme.LIGHT);
      matcher?.addEventListener('change', matchMediaHandler);
    }
    else {
      matcher?.removeEventListener('change', matchMediaHandler);
    }

    return () => {
      matcher?.removeEventListener('change', matchMediaHandler);
    };
  }, [systemTheme]);

  const value = {
    theme,
    toggleThemeState: getToggleThemeState(),
    setTheme: handleSetTheme,
    isSystemTheme: systemTheme,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
